import { useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { ArrowLeft, ChevronRight, Plus } from 'lucide-react'
import { Button } from '@/core/ui/Button'
import { useIsAuthenticated, useSession } from '@/core/auth/SessionStore'
import { useProjeto, useSessoesDoProjeto, type SessaoProducao } from '@/core/db/daos'
import { SyncStatusIndicator } from '@/features/common/SyncStatusIndicator'
import { cn } from '@/lib/cn'
import { useProducaoStore } from '@/features/producao/producaoStore'
import { loadSessionItensAsDrafts } from '@/features/producao/producaoRepo'
import type { SyncStatus } from '@/core/db/entities'

/**
 * Ponto de entrada do fluxo de Produção. Lista APENAS sessões com itens
 * pendentes de sincronização — sessões 100% SYNCED somem imediato.
 *
 * Comportamento (atualizado 2026-05-24):
 *  - Sessão com qualquer item PENDING/SYNCING/FAILED aparece.
 *  - Sessão 100% SYNCED some da lista IMEDIATAMENTE (não espera virar o dia).
 *  - Pra continuar adicionando lançamentos no mesmo dia/equipe, criar nova
 *    sessão — o backend faz merge por chave (projeto+dt+enc+equipe).
 *  - Tocar num card carrega os itens da sessão no store e navega pra Session.
 *
 * Sync rodando em segundo plano por engine global (foregroundSync). Removido
 * botão manual "Sincronizar" — o `SyncStatusIndicator` do header mostra o
 * estado e permite forçar ciclo se quiser.
 */
export function ProducaoListRoute() {
  const authed = useIsAuthenticated()
  const session = useSession()
  const navigate = useNavigate()
  const projeto = useProjeto(session?.projetoAtualId)

  const sessoes = useSessoesDoProjeto(session?.projetoAtualId)

  // Rascunho em memória — se o usuário voltou da Session sem finalizar.
  const rascunhoItens = useProducaoStore((s) => s.itens)
  const rascunhoProjetoId = useProducaoStore((s) => s.projetoId)
  const rascunhoEncarregadoNome = useProducaoStore((s) => s.encarregadoNome)
  const rascunhoEquipeNome = useProducaoStore((s) => s.equipeNome)
  const rascunhoDt = useProducaoStore((s) => s.dt)
  const rascunhoIsAtual =
    rascunhoItens.length > 0 && rascunhoProjetoId === session?.projetoAtualId

  const [opening, setOpening] = useState(false)

  if (!authed) return <Navigate to="/login" replace />
  if (session?.projetoAtualId == null) return <Navigate to="/" replace />

  function handleNovaSessao() {
    // CRÍTICO: limpa o rascunho em memória antes de navegar — sem isso, os itens
    // da sessão anterior aparecem "somados" na nova sessão.
    useProducaoStore.getState().reset()
    navigate('/producao/setup')
  }

  function handleContinuarRascunho() {
    navigate('/producao/session')
  }

  function handleDescartarRascunho() {
    if (
      !window.confirm(
        `Descartar ${rascunhoItens.length} apontamento(s) da sessão em construção? Esta ação não pode ser desfeita.`,
      )
    ) {
      return
    }
    useProducaoStore.getState().reset()
  }

  async function abrirSessao(s: SessaoProducao) {
    setOpening(true)
    try {
      const drafts = await loadSessionItensAsDrafts({
        projetoId: s.projetoId,
        dt: s.dt,
        encarregadoId: s.encarregadoId,
        equipeId: s.equipeId,
      })
      const store = useProducaoStore.getState()
      store.reset()
      store.setProjeto(s.projetoId, s.projetoCodigo)
      store.setEncarregado(s.encarregadoId, s.encarregadoNome)
      store.setEquipe(s.equipeId, s.equipeNome)
      store.setDt(s.dt)
      store.setItens(drafts)
      navigate('/producao/session')
    } catch (e) {
      console.error('[producao/list] abrirSessao falhou:', e)
      window.alert(e instanceof Error ? e.message : 'Falha ao carregar sessão')
    } finally {
      setOpening(false)
    }
  }

  const totalPendentes = (sessoes ?? []).reduce((acc, s) => acc + s.pendentes, 0)

  return (
    <main className="min-h-svh bg-bg-base flex flex-col">
      <header className="sticky top-0 z-10 bg-bg-base/95 backdrop-blur border-b border-border pt-safe pl-safe pr-safe">
        <div className="flex items-center gap-2 px-2 py-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/')} aria-label="Voltar">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold text-on-surface">Produção</h1>
            <p className="text-xs text-on-surface-dim truncate">
              {projeto?.codigo ?? '—'} · {(sessoes?.length ?? 0)} sessão(ões)
              {totalPendentes > 0 && ` · ${totalPendentes} pendente(s)`}
            </p>
          </div>
          <SyncStatusIndicator />
        </div>
      </header>

      <div className="flex-1 overflow-y-auto p-4 pb-32 max-w-2xl mx-auto w-full">
        {rascunhoIsAtual && (
          <div className="mb-4 rounded-xl border border-accent/40 bg-accent/10 p-4">
            <p className="text-xs font-bold text-accent uppercase">Sessão em construção</p>
            <p className="text-on-surface mt-1">
              <strong>{rascunhoItens.length}</strong> apontamento(s) não finalizado(s)
              {rascunhoEncarregadoNome &&
                ` · ${rascunhoEncarregadoNome}${rascunhoEquipeNome ? ` / ${rascunhoEquipeNome}` : ''}`}
              {rascunhoDt && ` · ${formatBR(rascunhoDt)}`}
            </p>
            <div className="mt-3 flex gap-2">
              <Button variant="primary" size="sm" onClick={handleContinuarRascunho} className="flex-1">
                Continuar
              </Button>
              <Button variant="outline" size="sm" onClick={handleDescartarRascunho}>
                Descartar
              </Button>
            </div>
          </div>
        )}

        {sessoes == null ? (
          <div className="text-center text-on-surface-dim mt-12">Carregando…</div>
        ) : sessoes.length === 0 && !rascunhoIsAtual ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <p className="text-on-surface font-medium">Nenhuma sessão hoje</p>
              <p className="text-sm text-on-surface-dim mt-2">
                Toque em "+ Nova sessão" para começar a apontar.
              </p>
            </div>
          </div>
        ) : sessoes.length > 0 ? (
          <>
            <p className="text-xs text-on-surface-dim mb-3">
              Toque numa sessão para ver/adicionar lançamentos. Sessões somem
              automaticamente quando 100% sincronizadas.
            </p>
            <ul className="space-y-3">
              {sessoes.map((s) => (
                <li key={s.key}>
                  <SessaoCard sessao={s} onClick={() => void abrirSessao(s)} disabled={opening} />
                </li>
              ))}
            </ul>
          </>
        ) : null}
      </div>

      <div className="sticky bottom-0 bg-bg-base/95 backdrop-blur border-t border-border p-4 pb-safe-plus-4 pl-safe pr-safe">
        <Button
          variant="primary"
          size="lg"
          onClick={handleNovaSessao}
          className="w-full gap-2"
        >
          <Plus className="h-5 w-5" />
          Nova sessão
        </Button>
      </div>
    </main>
  )
}

// ----------------------------------------------------------------------------
//  Card de sessão
// ----------------------------------------------------------------------------

function SessaoCard({
  sessao,
  onClick,
  disabled,
}: {
  sessao: SessaoProducao
  onClick: () => void
  disabled?: boolean
}) {
  const statusInfo: Record<SyncStatus, { label: string; cls: string }> = {
    PENDING: { label: 'pendente', cls: 'bg-warn/15 text-warn' },
    SYNCING: { label: 'enviando', cls: 'bg-info/15 text-info' },
    SYNCED: { label: 'sincronizado', cls: 'bg-ok/15 text-ok' },
    FAILED: { label: 'falhou', cls: 'bg-bad/15 text-bad' },
    // Estado do carreteiro; produção nunca chega nele (tipo compartilhado).
    REJEITADA: { label: 'recusada', cls: 'bg-bad/15 text-bad' },
  }
  const s = statusInfo[sessao.statusDominante]
  const reasons = [
    ...new Set(
      sessao.itens
        .filter((i) => i.syncStatus === 'FAILED' && i.failureReason)
        .map((i) => i.failureReason!),
    ),
  ]

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'card-surface p-4 flex items-start gap-3 w-full text-left',
        'transition-colors',
        disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-surface-1/70 active:bg-surface-1/50',
      )}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-on-surface font-medium">{formatBR(sessao.dt)}</p>
          <span
            className={cn(
              'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
              s.cls,
            )}
          >
            {s.label}
          </span>
        </div>
        <p className="text-sm text-on-surface-dim mt-1 truncate">
          {[sessao.encarregadoNome, sessao.equipeNome].filter(Boolean).join(' · ') ||
            '(sem encarregado/equipe)'}
        </p>
        <p className="text-sm text-on-surface mt-2">
          <span className="font-bold">{sessao.total}</span> apontamento(s)
          {sessao.pendentes > 0 && sessao.pendentes !== sessao.total && (
            <span className="text-warn"> · {sessao.pendentes} pendente(s)</span>
          )}
        </p>
        {reasons.length > 0 && (
          <p className="text-xs text-bad mt-1 truncate">Erro: {reasons.join('; ')}</p>
        )}
      </div>
      <ChevronRight className="h-5 w-5 text-on-surface-muted flex-shrink-0 mt-1" />
    </button>
  )
}

function formatBR(isoDate: string): string {
  const [y, m, d] = isoDate.split('-')
  return `${d}/${m}/${y}`
}
